'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { trackCTAClick } from '@/lib/analytics';

interface CTASectionProps {
  className?: string;
}

const CTASection = ({ className = '' }: CTASectionProps) => {
  return (
    <section className={`py-16 lg:py-24 bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-white relative overflow-hidden ${className}`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255, 215, 0, 0.3) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 bg-accent rounded-full mb-6 shadow-golden-lg">
            <Icon name="RocketLaunchIcon" size={40} className="text-primary" />
          </div>

          {/* Heading */}
          <h2 className="font-headline font-bold text-3xl lg:text-4xl xl:text-5xl mb-6">
            Ready to Partner with Africa's
            <span className="block mt-2 bg-gradient-to-r from-accent via-secondary to-accent bg-clip-text text-transparent">
              Premier Logistics Provider?
            </span>
          </h2>

          {/* Description */}
          <p className="font-body text-lg lg:text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Join leading mining operations, EPC firms, and regional traders who trust MylesDrive for their critical logistics needs across Africa.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/contact-and-quotes"
              onClick={() => trackCTAClick('Request a Quote', 'About Page', '/contact-and-quotes')}
              className="inline-flex items-center space-x-2 bg-accent hover:bg-secondary text-primary px-8 py-4 rounded-lg font-cta font-bold text-base shadow-golden-lg hover:shadow-golden-xl transition-all duration-300 hover:-translate-y-1 group"
            >
              <span>Request a Quote</span>
              <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link
              href="/services-overview"
              onClick={() => trackCTAClick('Explore Services', 'About Page', '/services-overview')}
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-8 py-4 rounded-lg font-cta font-bold text-base border-2 border-white/30 transition-all duration-300 hover:-translate-y-1"
            >
              <Icon name="TruckIcon" size={20} />
              <span>Explore Services</span>
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-16 pt-12 border-t border-white/20">
            {[
              { icon: 'ShieldCheckIcon', label: 'ISO Certified' },
              { icon: 'ClockIcon', label: '24/7 Support' },
              { icon: 'MapIcon', label: '6 Countries' },
              { icon: 'TruckIcon', label: '150+ Fleet' }
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center space-y-2">
                <Icon name={item.icon as any} size={32} className="text-accent" />
                <span className="font-body text-sm text-white/80">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;